"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReadableEthersLiquity = exports.BackendTroveStatus = void 0;
const lib_base_1 = require("@liquity/lib-base");
const _utils_1 = require("./_utils");
const EthersLiquityConnection_1 = require("./EthersLiquityConnection");
const BlockPolledLiquityStore_1 = require("./BlockPolledLiquityStore");
// TODO: these are constant in the contracts, so it doesn't make sense to make a call for them,
// but to avoid having to update them here when we change them in the contracts, we could read
// them once after deployment and save them to LiquityDeployment.
const MINUTE_DECAY_FACTOR = lib_base_1.Decimal.from("0.999037758833783000");
const BETA = lib_base_1.Decimal.from(2);
exports.BackendTroveStatus = {
    nonExistent: 0,
    active: 1,
    closedByOwner: 2,
    closedByLiquidation: 3,
    closedByRedemption: 4,
};
const userTroveStatusFrom = (backendStatus) => backendStatus === exports.BackendTroveStatus.nonExistent
    ? "nonExistent"
    : backendStatus === exports.BackendTroveStatus.active
        ? "open"
        : backendStatus === exports.BackendTroveStatus.closedByOwner
            ? "closedByOwner"
            : backendStatus === exports.BackendTroveStatus.closedByLiquidation
                ? "closedByLiquidation"
                : backendStatus === exports.BackendTroveStatus.closedByRedemption
                    ? "closedByRedemption"
                    : _utils_1.panic(new Error(`invalid backendStatus ${backendStatus}`));
const convertToDate = (timestamp) => new Date(timestamp * 1000);
const validSortingOptions = [
    "ascendingCollateralRatio",
    "descendingCollateralRatio",
];
const expectPositiveInt = (obj, key) => {
    if (obj[key] !== undefined) {
        if (!Number.isInteger(obj[key])) {
            throw new Error(`${key} must be an integer`);
        }
        if (obj[key] < 0) {
            throw new Error(`${key} must not be negative`);
        }
    }
};
/**
 * Ethers-based implementation of {@link @liquity/lib-base#ReadableLiquity}.
 *
 * @public
 */
class ReadableEthersLiquity {
    /** @internal */
    constructor(connection) {
        this.connection = connection;
    }
    /** @internal */
    static _from(connection) {
        const readable = new ReadableEthersLiquity(connection);
        return connection.useStore === "blockPolled"
            ? new _BlockPolledReadableEthersLiquity(readable)
            : readable;
    }
    /**
     * Connect to the Liquity protocol and create a `ReadableEthersLiquity` object.
     *
     * @param signerOrProvider - Ethers `Signer` or `Provider` to use for connecting to the Ethereum
     *                           network.
     * @param optionalParams - Optional parameters that can be used to customize the connection.
     */
    static async connect(signerOrProvider, optionalParams) {
        return ReadableEthersLiquity._from(await EthersLiquityConnection_1._connect(signerOrProvider, optionalParams));
    }
    hasStore() {
        return false;
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTotalRedistributed} */
    async getTotalRedistributed(overrides) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this.connection);
        const [collateral, debt] = await Promise.all([
            troveManager.L_ETH({ ...overrides }).then(_utils_1.decimalify),
            troveManager.L_LUSDDebt({ ...overrides }).then(_utils_1.decimalify),
        ]);
        return new lib_base_1.Trove(collateral, debt);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTroveBeforeRedistribution} */
    async getTroveBeforeRedistribution(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { troveManager } = EthersLiquityConnection_1._getContracts(this.connection);
        const [trove, snapshot] = await Promise.all([
            troveManager.Troves(address, { ...overrides }),
            troveManager.rewardSnapshots(address, { ...overrides }),
        ]);
        if (trove.status === exports.BackendTroveStatus.active) {
            return new lib_base_1.TroveWithPendingRedistribution(address, userTroveStatusFrom(trove.status), _utils_1.decimalify(trove.coll), _utils_1.decimalify(trove.debt), _utils_1.decimalify(trove.stake), new lib_base_1.Trove(_utils_1.decimalify(snapshot.ETH), _utils_1.decimalify(snapshot.LUSDDebt)));
        }
        else {
            return new lib_base_1.TroveWithPendingRedistribution(address, userTroveStatusFrom(trove.status));
        }
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTrove} */
    async getTrove(address, overrides) {
        const [trove, totalRedistributed] = await Promise.all([
            this.getTroveBeforeRedistribution(address, overrides),
            this.getTotalRedistributed(overrides),
        ]);
        return trove.applyRedistribution(totalRedistributed);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getNumberOfTroves} */
    async getNumberOfTroves(overrides) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this.connection);
        return (await troveManager.getTroveOwnersCount({ ...overrides })).toNumber();
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getPrice} */
    getPrice(overrides) {
        const { priceFeed } = EthersLiquityConnection_1._getContracts(this.connection);
        return priceFeed.callStatic.fetchPrice({ ...overrides }).then(_utils_1.decimalify);
    }
    /** @internal */
    async _getActivePool(overrides) {
        const { activePool } = EthersLiquityConnection_1._getContracts(this.connection);
        const [activeCollateral, activeDebt] = await Promise.all([
            activePool.getETH({ ...overrides }),
            activePool.getLUSDDebt({ ...overrides }),
        ].map((getBigNumber) => getBigNumber.then(_utils_1.decimalify)));
        return new lib_base_1.Trove(activeCollateral, activeDebt);
    }
    /** @internal */
    async _getDefaultPool(overrides) {
        const { defaultPool } = EthersLiquityConnection_1._getContracts(this.connection);
        const [liquidatedCollateral, closedDebt] = await Promise.all([
            defaultPool.getETH({ ...overrides }),
            defaultPool.getLUSDDebt({ ...overrides }),
        ].map((getBigNumber) => getBigNumber.then(_utils_1.decimalify)));
        return new lib_base_1.Trove(liquidatedCollateral, closedDebt);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTotal} */
    async getTotal(overrides) {
        const [activePool, defaultPool] = await Promise.all([
            this._getActivePool(overrides),
            this._getDefaultPool(overrides),
        ]);
        return activePool.add(defaultPool);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getStabilityDeposit} */
    async getStabilityDeposit(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { stabilityPool } = EthersLiquityConnection_1._getContracts(this.connection);
        const [{ frontEndTag, initialValue }, currentLUSD, collateralGain, lqtyReward,] = await Promise.all([
            stabilityPool.deposits(address, { ...overrides }),
            stabilityPool.getCompoundedLUSDDeposit(address, { ...overrides }),
            stabilityPool.getDepositorETHGain(address, { ...overrides }),
            stabilityPool.getDepositorLQTYGain(address, { ...overrides }),
        ]);
        return new lib_base_1.StabilityDeposit(_utils_1.decimalify(initialValue), _utils_1.decimalify(currentLUSD), _utils_1.decimalify(collateralGain), _utils_1.decimalify(lqtyReward), frontEndTag);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getRemainingStabilityPoolLQTYReward} */
    async getRemainingStabilityPoolLQTYReward(overrides) {
        const { communityIssuance } = EthersLiquityConnection_1._getContracts(this.connection);
        const issuanceCap = this.connection.totalStabilityPoolLQTYReward;
        const totalLQTYIssued = _utils_1.decimalify(await communityIssuance.totalLQTYIssued({ ...overrides }));
        // totalLQTYIssued approaches but never reaches issuanceCap
        return issuanceCap.sub(totalLQTYIssued);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getLUSDInStabilityPool} */
    getLUSDInStabilityPool(overrides) {
        const { stabilityPool } = EthersLiquityConnection_1._getContracts(this.connection);
        return stabilityPool
            .getTotalLUSDDeposits({ ...overrides })
            .then(_utils_1.decimalify);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getLUSDBalance} */
    getLUSDBalance(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { lusdToken } = EthersLiquityConnection_1._getContracts(this.connection);
        return lusdToken.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
    }
    /** Shinto: getPLXBalance {@inheritDoc @liquity/lib-base#ReadableLiquity.getPLXBalance} */
    getPLXBalance(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { plxToken } = EthersLiquityConnection_1._getContracts(this.connection); //TO-DO: plxToken abi & address needs to be added
        return plxToken.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getLQTYBalance} */
    getLQTYBalance(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { lqtyToken } = EthersLiquityConnection_1._getContracts(this.connection);
        return lqtyToken.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getUniTokenBalance} */
    getUniTokenBalance(pool, address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { uniToken, uniToken2, uniToken3 } = EthersLiquityConnection_1._getContracts(this.connection);
        // return Promise.resolve(Decimal.ZERO);
        if (pool == "unipool1") {
            return uniToken.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
        }
        else if (pool == "unipool2") {
            return uniToken2.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
        }
        else {
            return uniToken3.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
        }
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getUniTokenAllowance} */
    getUniTokenAllowance(pool, address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { uniToken, unipool, uniToken2, unipool2, unipool3, uniToken3, } = EthersLiquityConnection_1._getContracts(this.connection);
        // return Promise.resolve(Decimal.ZERO);
        if (pool == "unipool1") {
            return uniToken
                .allowance(address, unipool.address, { ...overrides })
                .then(_utils_1.decimalify);
        }
        else if (pool == "unipool2") {
            return uniToken2
                .allowance(address, unipool2.address, { ...overrides })
                .then(_utils_1.decimalify);
        }
        else {
            return uniToken3
                .allowance(address, unipool3.address, { ...overrides })
                .then(_utils_1.decimalify);
        }
    }
    /** Shinto: getPLSXTokenAllowance {@inheritDoc @liquity/lib-base#ReadableLiquity.getPLSXTokenAllowance} */
    async getPLSXTokenAllowance(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { plxToken, borrowerOperations } = EthersLiquityConnection_1._getContracts(this.connection);
        return plxToken
            .allowance(address, borrowerOperations.address, { ...overrides })
            .then(_utils_1.decimalify);
    }
    /** Shinto: getRedemptionAllowance {@inheritDoc @liquity/lib-base#ReadableLiquity.getRedemptionAllowance} */
    async getRedemptionAllowance(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { wrapperRedemption, lusdToken } = EthersLiquityConnection_1._getContracts(this.connection);
        return lusdToken
            .allowance(address, wrapperRedemption.address, { ...overrides })
            .then(_utils_1.decimalify);
    }
    /** Shinto: TO-DO getSacDetails {@inheritDoc @liquity/lib-base#ReadableLiquity.getSacDetails} */
    async getSacDetails(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        // return [[],[]]
        const { sacrificeVesting } = EthersLiquityConnection_1._getContracts(this.connection);
        const details = await sacrificeVesting.getUserDetails(address, {
            ...overrides,
        });
        const sacDetails = details.map((detail) => parseInt(detail._hex, 16));
        // const vestDetails = vestingDetails.map((detail) => parseInt(detail._hex, 16));
        // const vestingDetails = await vestingContract.getUserDetails(address, { ...overrides });
        return [sacDetails, []];
    }
    /*
    async getSacDetails(address?: string, overrides?: EthersCallOverrides): Promise<any> {
      address ??= _requireAddress(this.connection);
  
      return [ [0,0,0,0], [] ];
    }
    */
    /** @internal */
    async _getRemainingLiquidityMiningLQTYRewardCalculator(pool, overrides) {
        const { unipool, unipool2, unipool3 } = EthersLiquityConnection_1._getContracts(this.connection);
        if (pool == "unipool1") {
            const [totalSupply, rewardRate, periodFinish, lastUpdateTime,] = await Promise.all([
                unipool.totalSupply({ ...overrides }),
                unipool.rewardRate({ ...overrides }).then(_utils_1.decimalify),
                unipool.periodFinish({ ...overrides }).then(_utils_1.numberify),
                unipool.lastUpdateTime({ ...overrides }).then(_utils_1.numberify),
            ]);
            return (blockTimestamp) => rewardRate.mul(Math.max(0, periodFinish -
                (totalSupply.isZero() ? lastUpdateTime : blockTimestamp)));
        }
        else if (pool == "unipool2") {
            const [totalSupply, rewardRate, periodFinish, lastUpdateTime,] = await Promise.all([
                unipool2.totalSupply({ ...overrides }),
                unipool2.rewardRate({ ...overrides }).then(_utils_1.decimalify),
                unipool2.periodFinish({ ...overrides }).then(_utils_1.numberify),
                unipool2.lastUpdateTime({ ...overrides }).then(_utils_1.numberify),
            ]);
            return (blockTimestamp) => rewardRate.mul(Math.max(0, periodFinish -
                (totalSupply.isZero() ? lastUpdateTime : blockTimestamp)));
        }
        else {
            const [totalSupply, rewardRate, periodFinish, lastUpdateTime,] = await Promise.all([
                unipool3.totalSupply({ ...overrides }),
                unipool3.rewardRate({ ...overrides }).then(_utils_1.decimalify),
                unipool3.periodFinish({ ...overrides }).then(_utils_1.numberify),
                unipool3.lastUpdateTime({ ...overrides }).then(_utils_1.numberify),
            ]);
            return (blockTimestamp) => rewardRate.mul(Math.max(0, periodFinish -
                (totalSupply.isZero() ? lastUpdateTime : blockTimestamp)));
        }
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getRemainingLiquidityMiningLQTYReward} */
    async getRemainingLiquidityMiningLQTYReward(pool, overrides) {
        const [calculateRemainingLQTY, blockTimestamp] = await Promise.all([
            this._getRemainingLiquidityMiningLQTYRewardCalculator(pool, overrides),
            this._getBlockTimestamp(overrides === null || overrides === void 0 ? void 0 : overrides.blockTag),
        ]);
        return calculateRemainingLQTY(blockTimestamp);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getLiquidityMiningStake} */
    getLiquidityMiningStake(pool, address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { unipool, unipool2, unipool3 } = EthersLiquityConnection_1._getContracts(this.connection);
        if (pool == "unipool1") {
            return unipool.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
        }
        else if (pool == "unipool2") {
            return unipool2.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
        }
        else {
            return unipool3.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
        }
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTotalStakedUniTokens} */
    getTotalStakedUniTokens(pool, overrides) {
        const { unipool, unipool2, unipool3 } = EthersLiquityConnection_1._getContracts(this.connection);
        if (pool == "unipool1") {
            return unipool.totalSupply({ ...overrides }).then(_utils_1.decimalify);
        }
        else if (pool == "unipool2") {
            return unipool2.totalSupply({ ...overrides }).then(_utils_1.decimalify);
        }
        else {
            return unipool3.totalSupply({ ...overrides }).then(_utils_1.decimalify);
        }
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getLiquidityMiningLQTYReward} */
    getLiquidityMiningLQTYReward(pool, address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { unipool, unipool2, unipool3 } = EthersLiquityConnection_1._getContracts(this.connection);
        if (pool == "unipool1") {
            return unipool.earned(address, { ...overrides }).then(_utils_1.decimalify);
        }
        else if (pool == "unipool2") {
            return unipool2.earned(address, { ...overrides }).then(_utils_1.decimalify);
        }
        else {
            return unipool3.earned(address, { ...overrides }).then(_utils_1.decimalify);
        }
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getCollateralSurplusBalance} */
    getCollateralSurplusBalance(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { collSurplusPool } = EthersLiquityConnection_1._getContracts(this.connection);
        return collSurplusPool
            .getCollateral(address, { ...overrides })
            .then(_utils_1.decimalify);
    }
    async getTroves(params, overrides) {
        var _a, _b;
        const { multiTroveGetter } = EthersLiquityConnection_1._getContracts(this.connection);
        expectPositiveInt(params, "first");
        expectPositiveInt(params, "startingAt");
        if (!validSortingOptions.includes(params.sortedBy)) {
            throw new Error(`sortedBy must be one of: ${validSortingOptions
                .map((x) => `"${x}"`)
                .join(", ")}`);
        }
        const [totalRedistributed, backendTroves] = await Promise.all([
            params.beforeRedistribution
                ? undefined
                : this.getTotalRedistributed({ ...overrides }),
            multiTroveGetter.getMultipleSortedTroves(params.sortedBy === "descendingCollateralRatio"
                ? (_a = params.startingAt) !== null && _a !== void 0 ? _a : 0 : -(((_b = params.startingAt) !== null && _b !== void 0 ? _b : 0) + 1), params.first, { ...overrides }),
        ]);
        const troves = mapBackendTroves(backendTroves);
        if (totalRedistributed) {
            return troves.map((trove) => trove.applyRedistribution(totalRedistributed));
        }
        else {
            return troves;
        }
    }
    /** @internal */
    _getBlockTimestamp(blockTag) {
        return EthersLiquityConnection_1._getBlockTimestamp(this.connection, blockTag);
    }
    /** @internal */
    async _getFeesFactory(overrides) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this.connection);
        const [lastFeeOperationTime, baseRateWithoutDecay] = await Promise.all([
            troveManager.lastFeeOperationTime({ ...overrides }),
            troveManager.baseRate({ ...overrides }).then(_utils_1.decimalify),
        ]);
        return (blockTimestamp, recoveryMode) => new lib_base_1.Fees(baseRateWithoutDecay, MINUTE_DECAY_FACTOR, BETA, convertToDate(lastFeeOperationTime.toNumber()), convertToDate(blockTimestamp), recoveryMode);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getFees} */
    async getFees(overrides) {
        const [createFees, total, price, blockTimestamp] = await Promise.all([
            this._getFeesFactory(overrides),
            this.getTotal(overrides),
            this.getPrice(overrides),
            this._getBlockTimestamp(overrides === null || overrides === void 0 ? void 0 : overrides.blockTag),
        ]);
        return createFees(blockTimestamp, total.collateralRatioIsBelowCritical(price));
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getLQTYStake} */
    async getLQTYStake(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { lqtyStaking } = EthersLiquityConnection_1._getContracts(this.connection);
        const [stakedLQTY, collateralGain, lusdGain] = await Promise.all([
            lqtyStaking.stakes(address, { ...overrides }),
            lqtyStaking.getPendingETHGain(address, { ...overrides }),
            lqtyStaking.getPendingLUSDGain(address, { ...overrides }),
        ].map((getBigNumber) => getBigNumber.then(_utils_1.decimalify)));
        return new lib_base_1.LQTYStake(stakedLQTY, collateralGain, lusdGain);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTotalStakedLQTY} */
    async getTotalStakedLQTY(overrides) {
        const { lqtyStaking } = EthersLiquityConnection_1._getContracts(this.connection);
        return lqtyStaking.totalLQTYStaked({ ...overrides }).then(_utils_1.decimalify);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getFrontendStatus} */
    async getFrontendStatus(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireFrontendAddress(this.connection));
        const { stabilityPool } = EthersLiquityConnection_1._getContracts(this.connection);
        const { registered, kickbackRate } = await stabilityPool.frontEnds(address, { ...overrides });
        return registered
            ? { status: "registered", kickbackRate: _utils_1.decimalify(kickbackRate) }
            : { status: "unregistered" };
    }
}
exports.ReadableEthersLiquity = ReadableEthersLiquity;
const mapBackendTroves = (troves) => troves.map((trove) => new lib_base_1.TroveWithPendingRedistribution(trove.owner, "open", // These Troves are coming from the SortedTroves list, so they must be open
_utils_1.decimalify(trove.coll), _utils_1.decimalify(trove.debt), _utils_1.decimalify(trove.stake), new lib_base_1.Trove(_utils_1.decimalify(trove.snapshotETH), _utils_1.decimalify(trove.snapshotLUSDDebt))));
class _BlockPolledReadableEthersLiquity {
    constructor(readable) {
        const store = new BlockPolledLiquityStore_1.BlockPolledLiquityStore(readable);
        this.store = store;
        this.connection = readable.connection;
        this._readable = readable;
    }
    _blockHit(overrides) {
        return (!overrides ||
            overrides.blockTag === undefined ||
            overrides.blockTag === this.store.state.blockTag);
    }
    _userHit(address, overrides) {
        return (this._blockHit(overrides) &&
            (address === undefined || address === this.store.connection.userAddress));
    }
    _frontendHit(address, overrides) {
        return (this._blockHit(overrides) &&
            (address === undefined || address === this.store.connection.frontendTag));
    }
    hasStore(store) {
        return store === undefined || store === "blockPolled";
    }
    async getTotalRedistributed(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.totalRedistributed
            : this._readable.getTotalRedistributed(overrides);
    }
    async getTroveBeforeRedistribution(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.troveBeforeRedistribution
            : this._readable.getTroveBeforeRedistribution(address, overrides);
    }
    async getTrove(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.trove
            : this._readable.getTrove(address, overrides);
    }
    async getNumberOfTroves(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.numberOfTroves
            : this._readable.getNumberOfTroves(overrides);
    }
    async getPrice(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.price
            : this._readable.getPrice(overrides);
    }
    async getTotal(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.total
            : this._readable.getTotal(overrides);
    }
    async getStabilityDeposit(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.stabilityDeposit
            : this._readable.getStabilityDeposit(address, overrides);
    }
    async getRemainingStabilityPoolLQTYReward(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.remainingStabilityPoolLQTYReward
            : this._readable.getRemainingStabilityPoolLQTYReward(overrides);
    }
    async getLUSDInStabilityPool(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.lusdInStabilityPool
            : this._readable.getLUSDInStabilityPool(overrides);
    }
    async getLUSDBalance(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.lusdBalance
            : this._readable.getLUSDBalance(address, overrides);
    }
    //Shinto: getPLXBalance
    async getPLXBalance(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.accountBalance
            : this._readable.getPLXBalance(address, overrides);
    }
    async getLQTYBalance(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.lqtyBalance
            : this._readable.getLQTYBalance(address, overrides);
    }
    async getUniTokenBalance(pool, address, overrides) {
        return this._userHit(address, overrides)
            ? pool == "unipool1"
                ? this.store.state.uniTokenBalance
                : this.store.state.uniTokenBalance2
            : this._readable.getUniTokenBalance(pool, address, overrides);
    }
    async getUniTokenAllowance(pool, address, overrides) {
        return this._userHit(address, overrides)
            ? pool == "unipool1"
                ? this.store.state.uniTokenAllowance
                : this.store.state.uniTokenAllowance2
            : this._readable.getUniTokenAllowance(pool, address, overrides);
    }
    //Shinto: getPLSXTokenAllowance()
    async getPLSXTokenAllowance(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.plsxTokenAllowance
            : this._readable.getPLSXTokenAllowance(address, overrides);
    }
    //Shinto: getRedemptionAllowance()
    async getRedemptionAllowance(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.redemptionAllowance
            : this._readable.getRedemptionAllowance(address, overrides);
    }
    //Shinto: getSacDetails()
    async getSacDetails(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.sacrificeDetails
            : this._readable.getSacDetails(address, overrides);
    }
    async getRemainingLiquidityMiningLQTYReward(pool, overrides) {
        return this._blockHit(overrides)
            ? this.store.state.remainingLiquidityMiningLQTYReward
            : this._readable.getRemainingLiquidityMiningLQTYReward(pool, overrides);
    }
    async getLiquidityMiningStake(pool, address, overrides) {
        return this._userHit(address, overrides)
            ? pool == "unipool1"
                ? this.store.state.liquidityMiningStake
                : this.store.state.liquidityMiningStake2
            : this._readable.getLiquidityMiningStake(pool, address, overrides);
    }
    async getTotalStakedUniTokens(pool, overrides) {
        return this._blockHit(overrides)
            ? pool == "unipool1"
                ? this.store.state.totalStakedUniTokens
                : this.store.state.totalStakedUniTokens2
            : this._readable.getTotalStakedUniTokens(pool, overrides);
    }
    async getLiquidityMiningLQTYReward(pool, address, overrides) {
        return this._userHit(address, overrides)
            ? pool == "unipool1"
                ? this.store.state.liquidityMiningLQTYReward
                : this.store.state.liquidityMiningLQTYReward2
            : this._readable.getLiquidityMiningLQTYReward(pool, address, overrides);
    }
    async getCollateralSurplusBalance(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.collateralSurplusBalance
            : this._readable.getCollateralSurplusBalance(address, overrides);
    }
    async _getBlockTimestamp(blockTag) {
        return this._blockHit({ blockTag })
            ? this.store.state.blockTimestamp
            : this._readable._getBlockTimestamp(blockTag);
    }
    async _getFeesFactory(overrides) {
        return this._blockHit(overrides)
            ? this.store.state._feesFactory
            : this._readable._getFeesFactory(overrides);
    }
    async getFees(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.fees
            : this._readable.getFees(overrides);
    }
    async getLQTYStake(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.lqtyStake
            : this._readable.getLQTYStake(address, overrides);
    }
    async getTotalStakedLQTY(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.totalStakedLQTY
            : this._readable.getTotalStakedLQTY(overrides);
    }
    async getFrontendStatus(address, overrides) {
        return this._frontendHit(address, overrides)
            ? this.store.state.frontend
            : this._readable.getFrontendStatus(address, overrides);
    }
    getTroves(params, overrides) {
        return this._readable.getTroves(params, overrides);
    }
    _getActivePool() {
        throw new Error("Method not implemented.");
    }
    _getDefaultPool() {
        throw new Error("Method not implemented.");
    }
    _getRemainingLiquidityMiningLQTYRewardCalculator(pool) {
        throw new Error("Method not implemented.");
    }
}
//# sourceMappingURL=ReadableEthersLiquity.js.map